<template lang="pug">
.details
  .section-title {{ data?.label }}
  .details-fields(v-if="scheme.length > 0")
    .details-row(v-for="(row, index) in scheme", :key="`row-${index}`")
      .details-field(v-for="field in row", :key="field.name", :style="{ width: `${field.width}%` }")
        q-skeleton.field-value(v-if="fieldsLoading", animation="blink", :dark="theme === 'dark'")
        component.details-field-control(
          v-else-if="getFieldComponent(field.name)",
          :is="getFieldComponent(field.name)",
          v-bind="getFieldProps(field.name)",
          @change="() => {}",
          @reset="() => {}",
          @select-date="() => {}"
        )
        .field-value(v-else) {{ getFieldValue(field.name) }}
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { handleError } from "@/services/handleErrors";
import { theme } from "@/services/useTheme";
import backend from "@/api";
import fieldsComponents from "../../../general_components/fields/index.js";
import { useStore } from "@/store";
import { formatDateText } from "@/services/useDateTextFormatter";

const store = useStore();

const props = defineProps({
  /** Дата текущей секции. Пример: 
  {
    "type": "table",
    "data_url": "/acts/24",
    "components": {
      "fields": [
          {
              "label": "Внешний ID",
              "type": "string",
              "name": "external_id"
          },
          {
              "label": "Статус",
              "type": "string",
              "name": "status"
          },
          {
              "label": "Компания исполнителя",
              "type": "string",
              "name": "executor_company"
          },
          {
              "label": "Договор",
              "type": "string",
              "name": "contact_title"
          },
          {
              "label": "Тип заявки",
              "type": "string",
              "name": "issue_type"
          },
          {
              "label": "ID заявки",
              "type": "string",
              "name": "issue_id"
          },
          {
              "label": "Внешний ID заявки",
              "type": "string",
              "name": "issue_external_id"
          },
          {
              "label": "Дата создания",
              "type": "datetime",
              "format": "%H:%M %d.%m.%Y",
              "name": "created_at"
          },
          {
              "label": "Группа системы",
              "type": "string",
              "name": "group_system_title"
          },
          {
              "label": "Система",
              "type": "string",
              "name": "system_title"
          },
          {
              "label": "Оборудование",
              "type": "string",
              "name": "equipment_title"
          },
          {
              "label": "Общая стоимость",
              "type": "currency",
              "name": "total_cost"
          }
      ],
      "scheme": [
          [
              {
                  "name": "external_id",
                  "width": 33.3
              },
              {
                  "name": "status",
                  "width": 33.3
              },
              {
                  "name": "executor_company",
                  "width": 33.3
              }
          ],
          [
              {
                  "name": "contact_title",
                  "width": 50
              },
              {
                  "name": "issue_type",
                  "width": 50
              }
          ],
          [
              {
                  "name": "issue_id",
                  "width": 50
              },
              {
                  "name": "issue_external_id",
                  "width": 50
              }
          ],
          [
              {
                  "name": "created_at",
                  "width": 50
              },
              {
                  "name": "group_system_title",
                  "width": 50
              }
          ],
          [
              {
                  "name": "system_title",
                  "width": 50
              },
              {
                  "name": "equipment_title",
                  "width": 50
              }
          ],
          [
              {
                  "name": "total_cost",
                  "width": 100
              }
          ]
      ]
    }
  }
  */
  data: { type: Object, default: () => {} },
});

const fieldsData = ref({});
const fieldsLoading = ref(true);

const data = computed(() => props.data);
const fields = computed(() => data.value.components?.fields ?? []);
const scheme = computed(() => data.value.components?.scheme ?? []);
const path = computed(() => `/api/v3/${data.value.data_url}`);

const getField = name => {
  return fields.value.find(item => item.name === name);
};

const getRawFieldValue = name => fieldsData.value[name];

const getFieldValue = name => {
  const field = getField(name) ?? {};
  const type = getFieldType(field);
  const val = getRawFieldValue(name);

  if (val === null || val === undefined || val === "") return "";

  if (type === "date") {
    return formatDateText(val, "dd.MM.yyyy");
  }

  if (type === "datetime") {
    return formatDateText(val, "dd.MM.yyyy HH:mm");
  }

  if (Array.isArray(val)) {
    return val.map(item => (item && typeof item === "object" ? getOptionTitle(item) : item)).join(", ");
  }

  if (typeof val === "object") {
    return getOptionTitle(val);
  }

  return val;
};

const fieldComponentsByType = {
  string: fieldsComponents.string,
  text: fieldsComponents.text,
  select: fieldsComponents.select,
  checkbox: fieldsComponents.checkbox,
  file: fieldsComponents.file,
};

const getFieldType = field => {
  const type = field?.type ? String(field.type).toLowerCase() : "string";
  return type === "dropdown" || type === "radio" ? "select" : type;
};

const getFieldRenderType = name => {
  const field = getField(name);
  const type = getFieldType(field);

  return fieldComponentsByType[type] ? type : "string";
};

const getFieldComponent = name => {
  return fieldComponentsByType[getFieldRenderType(name)] ?? fieldsComponents.string;
};

const getOptionsPath = field => {
  if (field?.full_options_path) {
    return field.full_options_path;
  }

  return field?.options_path ? store.state.paths[field.options_path] : "";
};

const getOptionTitle = option => option.title ?? option.label ?? option.name ?? option.id ?? "";

const normalizeOption = option => ({
  ...option,
  title: getOptionTitle(option),
});

const normalizeSelectInitialValue = value => {
  if (Array.isArray(value)) {
    return value.map(item => (item && typeof item === "object" ? item.id : item));
  }

  return value && typeof value === "object" ? value.id : value;
};

const getSelectOptions = value => {
  if (Array.isArray(value)) {
    return value.filter(item => item && typeof item === "object").map(normalizeOption);
  }

  return value && typeof value === "object" ? [normalizeOption(value)] : [];
};

const getFieldProps = name => {
  const field = getField(name) ?? {};
  const type = getFieldRenderType(name);
  const rawValue = getRawFieldValue(name);
  const commonProps = {
    label: field.label,
    required: Boolean(field.required),
    readonly: true,
    placeholder: field.placeholder ?? "",
    hint: field.hint ?? null,
  };

  if (type === "select") {
    return {
      ...commonProps,
      initialValue: normalizeSelectInitialValue(rawValue),
      multiple: Boolean(field.multiple) || Array.isArray(rawValue),
      options: field.options?.length ? field.options.map(normalizeOption) : getSelectOptions(rawValue),
      optionsParams: field.options_params ?? {},
      fullOptionsPath: getOptionsPath(field),
      showCancel: false,
    };
  }

  if (type === "text") {
    return {
      ...commonProps,
      initialValue: getFieldValue(name),
      autogrow: true,
      formLoaded: true,
    };
  }

  if (type === "checkbox") {
    return {
      label: field.label,
      title: field.title ?? "",
      initialValue: Boolean(rawValue),
      required: Boolean(field.required),
      disable: true,
    };
  }

  if (type === "file") {
    return {
      ...commonProps,
      initialValue: null,
      existingValue: rawValue,
      readonly: true,
    };
  }

  return {
    ...commonProps,
    initialValue: getFieldValue(name),
  };
};

const normalizeDetailsData = responseData => responseData?.data ?? responseData;

const getDetailsData = async () => {
  try {
    const { data } = await backend.index(path.value, {});
    fieldsData.value = normalizeDetailsData(data);
  } catch (err) {
    await handleError(err);
  } finally {
    fieldsLoading.value = false;
  }
};

onMounted(async () => {
  await getDetailsData();
});
</script>

<style scoped lang="scss">
.details {
  .section-title {
    font-size: 16px;
    margin-top: 16px;
    font-weight: 600;
    color: var(--header-title-color);
  }

  .details-fields {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    color: var(--show-card-info-block-color);

    .details-row {
      display: flex;
      gap: 16px;
    }

    .field-label {
      font-weight: 600;
      font-size: 14px;
    }

    .field-value {
      margin-top: 8px;
      display: block;
      border: var(--show-card-info-block-border);
      padding: 10px;
      border-radius: 8px;
      word-break: break-word;
      min-height: 43px;
    }
  }
}
</style>
